Manage your services, integrations, and alert settings to ensure the right people are notified at the right time.

## Alert Deduplication

Alert deduplication prevents notification fatigue by grouping identical or related alerts.

### Deduplication Key Expiry

When an alert is created, it has a unique **Deduplication Key**.

- If a new alert arrives with the same key as an _existing open alert_, it is marked as a **duplicate** and does not trigger new notifications.
- If the existing alert has been open longer than the **Deduplication Key Expiry** time, the system "forgets" the old key. The new signal is treated as a fresh alert and **will** trigger notifications.

**Settings:**

- **Default:** 12 Hours
- **Options:** 1 Hour, 6 Hours, 24 Hours, 1 Week, or Custom (minutes).

## Notification Rules

Notification rules define _who_ gets notified and _when_ after an alert is triggered (unacknowledged).

### Configuring Rules

1.  Navigate to **Service Settings** > **Notification Rules**.
2.  Click **Add Rule**.
3.  Set the **Delay**: Time (in minutes) to wait after the alert is created before sending this notification. (0 minutes = immediate).
4.  Select **Contact Method**:
    - **SMS**: Send a text message.
    - **Voice**: Initiate a phone call.
    - **Email**: Send an email.
    - **Slack**: Post to a Slack channel (requires Slack integration).
    - **Webhook**: Trigger a generic webhook.
5.  Select **Severity** (Optional): Filter rules to only fire for specific alert severities (e.g., Critical only).

> [!TIP]
> Use a "staggered" approach. Example:
>
> - 0 min: Slack Channel
> - 5 min: On-Call User (SMS)
> - 15 min: Team Lead (Voice)

## Slack Integration

### Slack Channel (Notifications)

Send a notification to a specific Slack channel for **every** new alert created for this service.

- Updates (Ack/Close) will thread on the original message.
- **Setup:** Go to **Service Settings** > **Slack Channel** and select the channel.

### Slack Lists (Task Sync)

Sync internal alerts to a Slack List for better task tracking and visibility.

- **Enabled:** Automatically creates a customizable item in a Slack List when an alert is triggered.
- **Syncing:** Status changes in Gandalf (Open/Ack/Close) sync to the Slack List item status.

## Grafana Integration

Integrate Grafana to receive alerts directly in Gandalf.

### Setup Guide

1.  **In Gandalf Service**:
    - **Integration Keys** > **Create Key** > Type: **Grafana**.
    - Copy the **Webhook URL**.
2.  **In Grafana**:
    - **Alerting** > **Contact points** > **New**.
    - **Type:** Webhook.
    - **URL:** Paste the Gandalf URL.
    - **Test** & **Save**.

## Heartbeat Monitors

Ensure your external monitoring systems are actually running.

- Gandalf gives you a unique HTTPS URL.
- Configure your monitor to "ping" (GET/POST) this URL periodically.
- If Gandalf stops receiving pings (after the configured timeout), it creates an alert for **this service**.
